/**
 * Loading data, with the states the UI actually has to draw.
 *
 * A dedicated data-fetching library would be more capable, and would also be a
 * dependency carried for four screens. What is needed here is small and worth
 * owning: four explicit states, cancellation on unmount, and a `reload` that
 * screens call after they change something on the server.
 *
 * The states are kept distinct rather than collapsed into `data ?? loading`
 * because "loaded, and the answer is an empty list" and "still loading" look
 * completely different to a user, and conflating them produces the flash of
 * "Henüz proje yok" before the projects appear.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/api/errors";

export type AsyncState<T> =
  | { status: "idle"; data: null; error: null }
  | { status: "loading"; data: T | null; error: null }
  | { status: "success"; data: T; error: null }
  | { status: "error"; data: null; error: ApiError };

export interface AsyncResult<T> {
  state: AsyncState<T>;
  reload: () => void;
}

/**
 * `fetcher` must be stable - wrap it in `useCallback`. It receives an
 * `AbortSignal` and should pass it to the client so an in-flight request is
 * dropped when the component unmounts or the inputs change.
 */
export function useAsync<T>(fetcher: (signal: AbortSignal) => Promise<T>): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({
    status: "idle",
    data: null,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  // Guards against a slow first response overwriting a fast second one.
  const generation = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++generation.current;

    setState((previous) => ({
      status: "loading",
      // Keep the previous data visible while reloading: a list that blanks out
      // on every refresh is worse than one that updates in place.
      data: previous.status === "success" ? previous.data : null,
      error: null,
    }));

    fetcher(controller.signal)
      .then((data) => {
        if (current !== generation.current) return;
        setState({ status: "success", data, error: null });
      })
      .catch((error: unknown) => {
        if (current !== generation.current || controller.signal.aborted) return;
        setState({
          status: "error",
          data: null,
          error:
            error instanceof ApiError
              ? error
              : new ApiError({ kind: "unknown", url: "", cause: error }),
        });
      });

    return () => controller.abort();
  }, [fetcher, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { state, reload };
}

/**
 * The write-side counterpart: run an action, track whether it is in flight,
 * and keep the error so the screen can show it next to the button that caused
 * it rather than as a page-level failure.
 */
export function useAction<Args extends unknown[], T>(
  action: (...args: Args) => Promise<T>,
): {
  run: (...args: Args) => Promise<T | null>;
  busy: boolean;
  error: ApiError | null;
  reset: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args: Args): Promise<T | null> => {
      setBusy(true);
      setError(null);
      try {
        const result = await action(...args);
        return result;
      } catch (caught) {
        const apiError =
          caught instanceof ApiError
            ? caught
            : new ApiError({ kind: "unknown", url: "", cause: caught });
        if (mounted.current) setError(apiError);
        return null;
      } finally {
        if (mounted.current) setBusy(false);
      }
    },
    [action],
  );

  const reset = useCallback(() => setError(null), []);
  return { run, busy, error, reset };
}
